import { Modal } from "@/components/molecules";
import { useCompanyContext, useUserContext } from "@/providers/contexts";
import { Company } from "@/types/company";
import { useEffect, useState } from "react";
import { Attribute } from "../atoms";

interface CompanyDetailsModalProps {
  company: Company | null;
  companyModalOpen: boolean;
  loading: boolean;
  error?: string | null;
  onCloseButtonClick: () => void;
}

export const CompanyDetailsModal = ({
  company,
  companyModalOpen,
  onCloseButtonClick,
  loading,
  error,
}: CompanyDetailsModalProps) => {
  const {
    isAuthenticated,
    disabledBasicContent,
    disabledProContent,
    toggleFollowCompany,
    isCompanyFollowed,
    savedCompanies,
  } = useUserContext();

  const isDisabled =
    disabledBasicContent || disabledProContent || !isAuthenticated;

  const { setFlagCompanyModalOpen } = useCompanyContext();

  const companyId = company?.id ?? 0;
  const [isBookmarked, setIsBookmarked] = useState(
    isCompanyFollowed(companyId),
  );
  const [snackbar, setSnackbar] = useState({
    state: false,
    msg: "",
    severity: "success" as "success" | "error",
  });

  const handleBookmarkClick = () => {
    toggleFollowCompany(companyId);
    setSnackbar({
      state: true,
      msg: isBookmarked
        ? "Company removed from bookmarks"
        : "Company added to bookmarks",
      severity: "success",
    });
  };

  const handleFlagErrorClick = () => {
    setFlagCompanyModalOpen(true);
  };

  useEffect(() => {
    setIsBookmarked(isCompanyFollowed(companyId));
  }, [savedCompanies, companyId, isCompanyFollowed]);

  return (
    <Modal
      open={companyModalOpen && (companyId !== undefined || companyId !== 0)}
      componentName="company-details"
      header={company?.company_name || ""}
      logoUrl={company?.logo_url}
      loading={loading}
      error={error}
      showBookmark
      bookmarked={isBookmarked}
      disableBookmark={isDisabled}
      onBookmarkToggle={handleBookmarkClick}
      onCloseButtonClick={onCloseButtonClick}
      snackbar={{
        ...snackbar,
        onClose: () => setSnackbar(prev => ({ ...prev, state: false })),
      }}
      handleFlagErrorClick={handleFlagErrorClick}
    >
      <Attribute
        componentName="company-description"
        label="Company Description"
        value={company?.company_description ?? ""}
      />
      <Attribute
        componentName="company-url"
        label="Company URL"
        value={company?.company_url ?? ""}
        url={company?.company_url ?? ""}
      />

      {company?.linkedin_url && (
        <Attribute
          componentName="linkedin-url"
          label="LinkedIn URL"
          value={company?.linkedin_url}
          url={company?.linkedin_url}
        />
      )}

      {company?.company_type && (
        <Attribute
          componentName="company-type"
          label="Company Type"
          value={company?.company_type}
        />
      )}

      {company?.company_size && (
        <Attribute
          componentName="company-size"
          label="Company Size"
          value={company?.company_size}
        />
      )}

      {company?.funding_type && (
        <Attribute
          componentName="funding-type"
          label="Funding Type"
          value={company?.funding_type}
        />
      )}

      {company?.year_founded && (
        <Attribute
          componentName="year-founded"
          label="Year Founded"
          value={company?.year_founded.toString()}
        />
      )}
    </Modal>
  );
};
